import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { green, grey } from "@material-ui/core/colors";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import React, { useState, useEffect } from "react";
import { Deployment } from "../models/deployment";
import axios from "axios";
import { BackendURL } from "../globals";
import styles from "./DeploymentPage.module.css";
import { useParams } from "../util";
import { authToken } from "../components/Authentication";

export const deploymentPagePath =
  "/projects/:project_name/deployments/:deployment_id";

const apiRoot = `${BackendURL}/api`;

export default function DeploymentPage() {
  const { project_name, deployment_id } = useParams(deploymentPagePath);
  const [currentStep, setCurrentStep] = useState(0);
  const [jobStatus, setJobStatus] = useState({} as any);

  axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

  useEffect(() => {
    async function getDeploymentStatus() {
      const response = await axios.get(
        `${apiRoot}/jobs/${deployment_id}/status`
      );
      const deployment = {
        status: response.data.status,
        date_created: response.data.date_created,
      };
      setJobStatus(response.data);
      setCurrentStep(calculateCurrentStep(deployment));
    }
    getDeploymentStatus();
    const interval = setInterval(getDeploymentStatus, 5000);
    return () => clearInterval(interval);
  }, [deployment_id, project_name]);

  function calculateCurrentStep(deployment: Deployment) {
    if (!deployment.date_created) {
      return 0;
    } else if (deployment.status === "COMPLETED") {
      return 4;
    }
    const deploymentStart = new Date(deployment.date_created!);
    const minutesRunning = (Date.now() - deploymentStart.getTime()) / 60 / 1000;
    if (minutesRunning < 0.5) {
      return 0;
    } else if (minutesRunning < 4) {
      return 1;
    } else if (minutesRunning < 5) {
      return 2;
    } else {
      return 3;
    }
  }

  function getStepStatus(step: number) {
    if (currentStep > step) {
      return "completed";
    } else if (currentStep < step) {
      return "not_started";
    } else {
      return "in_progress";
    }
  }

  const StepIcon = ({
    stepStatus,
  }: {
    stepStatus: "not_started" | "in_progress" | "completed";
  }) => {
    if (stepStatus === "completed") {
      return <CheckCircleIcon fontSize="large" style={{ color: green[500] }} />;
    } else if (stepStatus === "in_progress") {
      return (
        <CircularProgress
          size="1.8rem"
          style={{ color: green[500], marginLeft: "3px", marginRight: "3px" }}
        />
      );
    } else {
      return (
        <RadioButtonUncheckedIcon
          fontSize="large"
          style={{ color: grey[500] }}
        />
      );
    }
  };

  const progressView = (
    <>
      <Grid container justify="center">
        <Grid item>
          <Card variant="outlined">
            <CardHeader title="Your first environment is being created" />
            <CardContent>
              <Typography variant="body1">
                <StepIcon stepStatus={getStepStatus(0)} />
                <span className={styles.step}>
                  Generating Terraform infrastructure-as-code
                </span>
              </Typography>
              <Typography variant="body1">
                <StepIcon stepStatus={getStepStatus(1)} />
                <span className={styles.step}>
                  Provisioning infrastructure in your AWS account
                </span>
              </Typography>
              <Typography variant="body1">
                <StepIcon stepStatus={getStepStatus(2)} />
                <span className={styles.step}>
                  Building and pushing Docker images
                </span>
              </Typography>
              <Typography variant="body1">
                <StepIcon stepStatus={getStepStatus(3)} />
                <span className={styles.step}>
                  Deploying applications and services
                </span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  if (!currentStep || currentStep < 4) {
    return progressView;
  } else {
    const serviceName = Object.keys(jobStatus.services)[0];
    const service = jobStatus.services[serviceName];
    const lb_url = `http://${service.lb_url}`;
    const successView = (
      <>
        <Grid container justify="center">
          <Grid item>
            <Card variant="outlined">
              <CardHeader title="Your environment is ready!" />
              <CardContent>
                <Typography variant="body1">
                  {serviceName}: <a href={lb_url}>{lb_url}</a>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    );
    return successView;
  }
}
